// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isAtcivatedWatchMangeOraganisation } from '@/refs/manageOrgCommerce.ref';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useManageOrganisationStore } from '@/store/manageOrganisation.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type {
  ICreateOrganisationBody,
  IUpdateOrganisationBody,
} from '@/types/manageOrgCommerce.types';

/**
 * Hook pour gérer les mutations liées aux organisations.
 * Fournit des fonctions pour créer, mettre à jour, désactiver
 * et mettre à jour le logo d'une organisation.
 */
export const ManageOrganisationMutation = () => {
  const organisationStore = useManageOrganisationStore();

  // -------------------------------
  // 🔹 Gestion des erreurs globale
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0];

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    isAtcivatedWatchMangeOraganisation.value =
      !isAtcivatedWatchMangeOraganisation.value;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'une organisation
  // -------------------------------
  const createOrganisation = useMutation<any, Error, ICreateOrganisationBody>({
    mutationFn: (payload: ICreateOrganisationBody) =>
      organisationStore.createOrganisation(payload),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Mise à jour d'une organisation
  // -------------------------------
  const updateOrganisation = useMutation<
    any,
    Error,
    { organizationId: string; data: IUpdateOrganisationBody }
  >({
    mutationFn: ({ organizationId, data }) =>
      organisationStore.updateOrganisation(organizationId, data),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Désactivation d'une organisation
  // -------------------------------
  const deactivateOrganisation = useMutation<any, Error, string>({
    mutationFn: (organizationId: string) =>
      organisationStore.deactivateOrganisation(organizationId),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Mise à jour du logo d'une organisation (upload fichier multipart)
  // -------------------------------
  const updateOrganisationLogo = useMutation<
    any,
    Error,
    { organizationId: string; file: File }
  >({
    mutationFn: ({ organizationId, file }) =>
      organisationStore.updateOrganisationLogo(organizationId, file),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Association d'un logo existant (par logoId JSON)
  // -------------------------------
  const setOrganisationLogoId = useMutation<
    any,
    Error,
    { organizationId: string; logoId: string | null }
  >({
    mutationFn: ({ organizationId, logoId }) =>
      organisationStore.setOrganisationLogoId(organizationId, logoId),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Sauvegarde complète (nom + logo fichier)
  // -------------------------------
  const saveOrganisation = useMutation<
    any,
    Error,
    {
      organizationId: string;
      data: IUpdateOrganisationBody;
      file?: File | null;
    }
  >({
    mutationFn: async ({ organizationId, data, file }) => {
      await organisationStore.updateOrganisation(organizationId, data);
      if (file) {
        await organisationStore.updateOrganisationLogo(organizationId, file);
      }
    },
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  const handleCreateOrganisation = (payload: ICreateOrganisationBody) =>
    createOrganisation.mutateAsync(payload);

  const handleUpdateOrganisation = (
    organizationId: string,
    data: IUpdateOrganisationBody,
  ) => updateOrganisation.mutateAsync({ organizationId, data });

  const handleDeactivateOrganisation = (organizationId: string) =>
    deactivateOrganisation.mutateAsync(organizationId);

  const handleUpdateOrganisationLogo = (organizationId: string, file: File) =>
    updateOrganisationLogo.mutateAsync({ organizationId, file });

  const handleSetOrganisationLogoId = (
    organizationId: string,
    logoId: string | null,
  ) => setOrganisationLogoId.mutateAsync({ organizationId, logoId });

  const handleSaveOrganisation = (
    organizationId: string,
    data: IUpdateOrganisationBody,
    file?: File | null,
  ) => saveOrganisation.mutateAsync({ organizationId, data, file });

  return {
    // mutations (pour accéder à isPending, isError, etc.)
    createOrganisation,
    updateOrganisation,
    deactivateOrganisation,
    updateOrganisationLogo,
    setOrganisationLogoId,
    saveOrganisation,
    // handlers
    handleCreateOrganisation,
    handleUpdateOrganisation,
    handleDeactivateOrganisation,
    handleUpdateOrganisationLogo,
    handleSetOrganisationLogoId,
    handleSaveOrganisation,
  };
};
